import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal } from "react-bootstrap";
import { TxItemText } from "./TxItemText";
import { CoinBadge } from "@components/CoinBadge";
import { useCoins } from "@contexts/CoinsContext";
import { useSelectedChain } from "@app/examples/ChainSelector";
import { TxModalContext } from "@contexts/TxModalContext";
import useAlert from "@hooks/AlertHook";
import { useAccount } from "@contexts/AccountContext";
import { useWallet } from "@providers/wallet.provider";
import { UnsignedTx } from "@goblinhunt/cosmes/wallet";

export function TxModal() {
  const { wasmTx, callback, hide } = useContext(TxModalContext);
  const { getCoin } = useCoins();
  const { selectedChain: chain } = useSelectedChain();
  const { wallet } = useAccount();
  const { post, estimateFee } = useWallet();
  const alert = useAlert();

  const [fee, setFee] = useState<{ denom: string; amount: string } | null>(
    null
  );
  const canConfirm = !!fee; // ✅ cuma aktif kalau fee ada

  const [total, setTotal] = useState<number>(0);

  const open = !!wasmTx;
  const timeoutRef = useRef<number | null>(null);

  const clearTimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const close = useCallback(
    (err?: Error) => {
      clearTimers();
      setFee(null);
      setTotal(0);
      hide(err ?? new Error("Transaction closed by user"));
    },
    [hide]
  );

  useEffect(() => () => clearTimers(), []);

  // Hitung fee + total hanya saat modal terbuka & msgs ada
  useEffect(() => {
    const run = async () => {
      if (!open || !wasmTx?.msgs) return;

      console.log(wasmTx)
      // Estimasi fee
      const est = await estimateFee(wasmTx);
      console.log(est.amount[0].amount);
      const picked = est.amount[0];
      setFee(picked ?? null);

      // Total = sum(funds) + fee
      let sum = 0;
      for (const m of wasmTx.msgs as any[]) {
        const funds = m?.data?.funds;
        if (Array.isArray(funds) && funds.length > 0) { 
          // asumsi single coin
          const c = funds[0];
          sum += Number(c?.amount ?? 0);
        }
      }
      if (picked) sum += Number(picked.amount);
      setTotal(sum);
    };

    run().catch(console.error);
  }, [open, wasmTx]);

  const subpost = async () => {
    try {
      if (!wasmTx || !chain)
        throw new Error(
          "Cannot get information from blockchain, try again later"
        );
      if (!wallet)
        throw new Error(
          "Cannot get your wallet information, try to refresh the page"
        );
      if (!fee) throw new Error("Please select fee denom");

      alert.loading(
        "Sign Transaction, if your wallet show nothing try to refresh the page"
      );

      const hash = await post({ msgs: wasmTx.msgs, memo: wasmTx.memo });

      timeoutRef.current = window.setTimeout(() => {
        alert.hide();
        hide();
        callback?.onSuccess?.call(null, hash);
      }, 800); // nggak usah lama-lama
    } catch (e) {
      console.error(e);
      close(e as Error);
    }
  };

  return (
    <Modal
      key={
        open ? (wasmTx as any)?.id ?? wasmTx?.memo ?? "tx-open" : "tx-closed"
      } // remount tiap siklus
      show={open}
      onHide={() => close(new Error("Transaction closed by user"))}
      backdrop="static" // tetap static (klik overlay tidak menutup) — ubah ke true kalau mau bisa close via overlay
      keyboard // ESC menutup
      restoreFocus
      enforceFocus
      dialogClassName="max-w-xl mx-auto" // ✅ center horizontal
      scrollable
    >
      {/* Pakai close bawaan biar transisi nggak ngaco */}
      <Modal.Header closeButton>
        <Modal.Title>{wasmTx?.memo ?? "Transaction"}</Modal.Title>
        <button
          type="button"
          className="btn-close"
          onClick={() => close(new Error("Transaction closed by user"))}
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            className="h-6 w-6 fill-jacarta-700 dark:fill-white"
          >
            <path fill="none" d="M0 0h24v24H0z" />
            <path d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z" />
          </svg>
        </button>
      </Modal.Header>

      <Modal.Body>
        <div className="p-6 text-center">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-display text-sm font-semibold text-jacarta-700 dark:text-white">
              Item
            </span>
            <span className="font-display text-sm font-semibold text-jacarta-700 dark:text-white">
              Subtotal
            </span>
          </div>

          {Array.isArray(wasmTx?.msgs) &&
            wasmTx!.msgs.map((data: any, index: number) => (
              <div
                key={index}
                className="jacarta-100 flex border-b border-t py-4 dark:border-jacarta-600"
              >
                <TxItemText text={wasmTx!.memo} />
                <div className="ml-auto">
                  {data?.data?.funds?.length ? (
                    data.data.funds.map((coin: any) => {
                      const coinDetail = getCoin(coin.denom);
                      if (!coinDetail) return null;
                      return (
                        <CoinBadge
                          key={`${coin.denom}-${index}`}
                          size="sm"
                          name={coinDetail.name}
                          icon={coinDetail.icon}
                          amount={Number(coin.amount)}
                        />
                      );
                    })
                  ) : (
                    <span className="text-white">-</span>
                  )}
                </div>
              </div>
            ))}

          <div className="flex items-center border-b border-jacarta-100 py-4 dark:border-jacarta-600">
            <TxItemText text="Fee" size="lg" />
            <div className="ml-auto">
              {fee && getCoin(fee.denom) && (
                <CoinBadge
                  key={"fee"}
                  size="sm"
                  name={getCoin(fee.denom)!.name}
                  icon={getCoin(fee.denom)!.icon}
                  amount={Number(fee.amount)}
                  color="jacarta-500"
                />
              )}
            </div>
          </div>

          <div className="mt-2 flex border-jacarta-100 py-4 dark:border-jacarta-600">
            <TxItemText text="Total" size="lg" />
            <div className="ml-auto">
              {fee && total && getCoin(fee.denom) && (
                <CoinBadge
                  key={"total"}
                  size="sm"
                  name={getCoin(fee.denom)!.name}
                  icon={getCoin(fee.denom)!.icon}
                  amount={total}
                  color="jacarta-500"
                />
              )}
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex items-center justify-center space-x-4">
          <button
            onClick={subpost}
            type="button"
            disabled={!canConfirm}
            title={!canConfirm ? "Menunggu estimasi fee..." : "Kirim transaksi"}
            className={`rounded-full px-8 py-3 text-center font-semibold text-white transition-all
    ${
      canConfirm
        ? "cursor-pointer bg-jacarta-500 hover:bg-jacarta-600"
        : "cursor-not-allowed bg-jacarta-500/60"
    }`}
          >
            Confirm
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
